/**
 * StandaloneInvoiceForm.jsx — Create invoice from Invoices page
 *
 * Unlike InvoiceForm (job-scoped), this form allows:
 * - Optional job picker (dropdown of active jobs)
 * - Manual client name entry (if no job selected)
 * - Invoice type: Deposit, Balance, Full
 * - Auto-calculated amounts when job is selected
 * - Manual amount entry when no job is linked
 * - Due date, terms, notes
 */

import { useState, useMemo, useEffect } from 'react';
import { uid, fmtC } from '../../lib/utils';
import { defaultDueDate, termsLabel } from '../../lib/invoiceDefaults';
import { useCreateInvoice, useInvoices, useInvoicesByJob } from '../../hooks/useInvoices';
import { useJobs } from '../../hooks/useJobs';
import { useSettings } from '../../hooks/useSettings';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';

export default function StandaloneInvoiceForm({ onSave, onCancel }) {
  const { toast } = useToast();
  const { data: settings } = useSettings();
  const { data: allInvoices = [] } = useInvoices();
  const { data: allJobs = [] } = useJobs();
  const createMutation = useCreateInvoice();
  const { data: xeroStatus } = useXeroStatus();
  const syncMutation = useSyncInvoiceToXero();
  const xeroConnected = xeroStatus?.connected === true;

  // ── Form state ───────────────────────────────────────────────────────────
  const [selectedJobId, setSelectedJobId] = useState('');
  const [client, setClient] = useState('');
  const [invoiceType, setInvoiceType] = useState('full');
  const [manualAmount, setManualAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(() => defaultDueDate(invoiceType, settings));

  // When invoice type changes (incl. handleJobChange auto-select), update due date:
  // deposits due immediately, others use final terms. Keyed on the derived string,
  // not the settings object.
  const typeDueDate = defaultDueDate(invoiceType, settings);
  useEffect(() => {
    setDueDate(typeDueDate);
  }, [typeDueDate]);

  const selectedJob = allJobs.find((j) => j.id === selectedJobId) || null;

  // ── Existing invoices for selected job ──────────────────────────────────
  const jobInvoices = allInvoices.filter((inv) => inv.jobId === selectedJobId && inv.status !== 'void');
  const existingDeposit = jobInvoices.find((inv) => inv.invoiceType === 'deposit');
  const existingBalance = jobInvoices.find((inv) => inv.invoiceType === 'balance');

  // ── Invoice number generation (PREFIX-YYMMDD-SEQ, daily reset) ───────────
  const nextNumber = useMemo(() => {
    const prefix = settings?.data?.invoicePrefix || 'INV';
    const startNum = parseInt(settings?.data?.invoiceStart || '1', 10) || 1;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const dateStr = `${yy}${mm}${dd}`;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-${dateStr}-(\\d+)$`);
    let maxNum = startNum - 1;
    allInvoices.forEach((inv) => {
      const match = (inv.number || '').match(pattern);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    return `${prefix}-${dateStr}-${String(maxNum + 1).padStart(3, '0')}`;
  }, [settings, allInvoices]);

  // ── Amount calculation ───────────────────────────────────────────────────
  const depositPct = parseFloat(settings?.data?.depositPct || 50);
  const jobRevenue = selectedJob
    ? parseFloat(selectedJob.revenue || 0)
    : 0;

  const calculatedAmount = useMemo(() => {
    if (!selectedJob) return parseFloat(manualAmount) || 0;
    if (invoiceType === 'deposit') {
      return Math.round(jobRevenue * (depositPct / 100) * 100) / 100;
    }
    if (invoiceType === 'balance') {
      const depositAmt = existingDeposit ? parseFloat(existingDeposit.amount || 0)
        : Math.round(jobRevenue * (depositPct / 100) * 100) / 100;
      return Math.round((jobRevenue - depositAmt) * 100) / 100;
    }
    return jobRevenue;
  }, [selectedJob, invoiceType, jobRevenue, depositPct, existingDeposit, manualAmount]);

  // ── When job changes, auto-fill client ──────────────────────────────────
  const handleJobChange = (jobId) => {
    setSelectedJobId(jobId);
    if (jobId) {
      const job = allJobs.find((j) => j.id === jobId);
      if (job) {
        setClient(job.clientName || job.client || '');
        // Auto-select best invoice type
        const jInvs = allInvoices.filter((inv) => inv.jobId === jobId && inv.status !== 'void');
        const hasDep = jInvs.some((inv) => inv.invoiceType === 'deposit');
        const hasBal = jInvs.some((inv) => inv.invoiceType === 'balance');
        if (!hasDep) setInvoiceType('deposit');
        else if (!hasBal) setInvoiceType('balance');
        else setInvoiceType('full');
      }
    } else {
      setInvoiceType('full');
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = selectedJob ? calculatedAmount : parseFloat(manualAmount);

    if (!client.trim()) {
      toast('Client name is required', 'error');
      return;
    }
    if (!amount || amount <= 0) {
      toast('Amount must be greater than zero', 'error');
      return;
    }

    const invoice = {
      id: uid(),
      number: nextNumber,
      jobId: selectedJobId || null,
      client: client.trim(),
      invoiceType,
      amount,
      paidAmount: 0,
      status: 'unpaid',
      terms: termsLabel(invoiceType, settings),
      dueDate,
      notes: notes.trim() || null,
      payments: [],
    };

    try {
      await createMutation.mutateAsync(invoice);
      toast(`Invoice ${nextNumber} created — ${fmtC(amount)}`);
      if (xeroConnected) syncMutation.mutate(invoice.id);
      onSave?.();
    } catch (err) {
      toast(`Failed to create invoice: ${err.message}`, 'error');
    }
  };

  // ── Active jobs for dropdown ─────────────────────────────────────────────
  const activeJobs = allJobs
    .filter((j) => !['void', 'archived'].includes(j.status))
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));

  const typeOptions = selectedJob ? [
    { value: 'deposit', label: `Deposit (${depositPct}%)`, disabled: !!existingDeposit, hint: existingDeposit ? 'Already created' : null },
    // Balance only makes sense when a deposit already exists. Hide it otherwise
    // so the user doesn't accidentally mislabel a full invoice as a balance.
    ...(existingDeposit
      ? [{ value: 'balance', label: 'Balance', disabled: !!existingBalance, hint: existingBalance ? 'Already created' : null }]
      : []),
    { value: 'full', label: 'Full Amount', disabled: false, hint: null },
  ] : [
    { value: 'full', label: 'Full Amount', disabled: false, hint: null },
  ];

  return (
    <form onSubmit={handleSubmit} className="card p-5 space-y-4 max-h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-heading text-text-primary">New Invoice</h3>
        <span className="text-2xs text-text-dim font-mono">{nextNumber}</span>
      </div>

      {/* Job picker */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">
          Link to Job <span className="font-normal">(optional)</span>
        </label>
        <select
          value={selectedJobId}
          onChange={(e) => handleJobChange(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
        >
          <option value="">No linked job — manual entry</option>
          {activeJobs.map((job) => (
            <option key={job.id} value={job.id}>
              {job.title} — {job.clientName || 'No client'} ({fmtC(job.revenue)})
            </option>
          ))}
        </select>
      </div>

      {/* Client name */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">
          Client <span className="text-red-accent">*</span>
        </label>
        <input
          type="text"
          value={client}
          onChange={(e) => setClient(e.target.value)}
          placeholder="Client or company name"
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Invoice type (only show multi-option when job linked) */}
      {selectedJob && (
        <div>
          <label className="block text-2xs font-semibold text-text-dim mb-2 uppercase">Invoice Type</label>
          <div className={`grid gap-2 ${typeOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {typeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={opt.disabled}
                onClick={() => !opt.disabled && setInvoiceType(opt.value)}
                className={`p-2 rounded border text-xs text-center transition-colors ${
                  invoiceType === opt.value
                    ? 'bg-accent/10 border-accent text-accent font-semibold'
                    : opt.disabled
                      ? 'bg-surface border-border text-text-dim opacity-50 cursor-not-allowed'
                      : 'bg-surface border-border text-text-primary hover:border-accent/50'
                }`}
              >
                <div>{opt.label}</div>
                {opt.hint && <div className="text-2xs text-text-dim mt-0.5">{opt.hint}</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Amount */}
      {selectedJob ? (
        <div className="bg-accent/5 border border-accent/20 rounded p-3 text-center">
          <div className="text-2xs text-text-dim mb-1">Invoice Amount</div>
          <div className="text-2xl font-heading text-accent">{fmtC(calculatedAmount)}</div>
          {invoiceType === 'deposit' && (
            <div className="text-2xs text-text-dim mt-1">{depositPct}% of {fmtC(jobRevenue)}</div>
          )}
          {invoiceType === 'balance' && existingDeposit && (
            <div className="text-2xs text-text-dim mt-1">{fmtC(jobRevenue)} − {fmtC(existingDeposit.amount)} deposit</div>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">
            Amount <span className="text-red-accent">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim text-sm">$</span>
            <input
              type="number"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full pl-7 pr-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
            />
          </div>
        </div>
      )}

      {/* Due date */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">Due Date</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes..."
          maxLength={500}
          rows={2}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent transition-colors resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="flex-1 px-3 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {createMutation.isPending ? 'Creating...' : 'Create Invoice'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
